<!-- eslint-disable max-len -->
<template>
  <ais-index
    :app-id="$store.state.algolia_app_id"
    :api-key="$store.state.algolia_api_key"
    :index-name="index"
    :query-parameters="{
      distinct: true
    }"
    id="recordView"
    class="page"
  >
    <SearchHero></SearchHero>
    <section class="hero is-medium is-paddingless">
      <GmapMap
        :center="$store.state.latlng"
        :zoom="13"
        :options="{ disableDefaultUI: true, maxZoom: 16 }"
        ref="mapRef"
        style="height: 500px; top: 4rem; margin-bottom: 4rem;"
      >
        <GmapMarker
          :key="index"
          v-for="(m, index) in markers"
          :position="m"
        ></GmapMarker>
      </GmapMap>
    </section>
    <section id="profile" class="columns is-centered">
      <div class="column is-10 profile-shadow">
        <div class="columns is-centered">
          <div class="column is-7 columns is-multiline">
            <div
              id="image-profile-section"
              class="column is-5 profile-section"
              :style="record.image_uri ? '' : 'order: 2'"
            >
              <div id="profile-image" v-if="record.image_uri" class="has-text-centered">
                <img :src="record.image_uri" :alt="record.name">
                <div
                  class="tag is-small is-sponsored-badge
                  has-background-primary has-text-white has-text-weight-semibold"
                  v-if="record.badge"
                >
                  {{ directoryBadge }}
                </div>
                <div
                  class="tag is-small is-expert-badge
                  has-background-danger has-text-white has-text-weight-semibold"
                  v-if="expert.enabled && record.expert"
                >
                  Expert
                </div>
              </div>

              <h1 class="title is-hidden-desktop is-hidden-tablet-only">{{ record.name }}</h1>
              <p
                class="subtitle is-hidden-desktop is-hidden-tablet-only"
                v-if="record[currentDirectory.secondary_search_data_point]"
              >
                {{ record[currentDirectory.secondary_search_data_point][0] }}
              </p>

              <div class="standard-data">
                <p class="address" v-if="record.locations.length > 0">
                  <span class="is-block">{{ record.locations[0].name || '' }}</span>
                  <span class="is-block">{{ record.locations[0].address || '' }}</span>
                  <span class="is-block">
                    {{ record.locations[0].city || '' }},
                    {{ record.locations[0].state || '' }}
                    {{ record.locations[0].zip || '' }}
                  </span>
                </p>
                <p
                  v-if="record.locations.length > 0 && record.locations[0].phone"
                  class="is-together result-phone"
                >
                  <fa icon="phone"></fa>
                  <a :href="`tel:${record.locations[0].phone}`"> {{ record.locations[0].phone }}</a>
                </p>

                <p v-if="record.profile_uri" class="is-together">
                  <fa icon="globe"></fa>
                  <a
                    :href="website(record.profile_uri)"
                    target="_blank"> {{ hostname(record.profile_uri) }}</a>
                </p>
              </div>
            </div>
            <div
              id="name-profile-section"
              class="column profile-section"
              :style="record.image_uri ? '' : 'order: 1'"
            >
              <div
                class="tag is-small is-expert-badge
                has-background-danger has-text-white has-text-weight-semibold"
                v-if="expert.enabled && record.expert && !record.image_uri"
              >
                Expert
              </div>
              <h1 class="title is-hidden-mobile">{{ record.name }}</h1>

              <p
                class="subtitle is-hidden-mobile"
                v-if="record[currentDirectory.secondary_search_data_point]"
              >
                <fa icon="chevron-right" class="fa-fw"></fa>
                {{ secondaryDataPoint }}
              </p>

              <div
                v-for="facet in validFacets"
                :key="facet.key"
              >
                <h2 class="has-text-weight-bold">{{ facet.name.toUpperCase() }}</h2>
                <div class="is-facet">
                  <p v-if="facet.type === 'string'">
                    <router-link
                      :to="`/${directory(facet)}?query=${record[facet.key]}`"
                      @click.native="scrollToTop"
                      v-if="facet.crossdirectory"
                    >{{ record[facet.key] }}</router-link>
                    <router-link
                      :to="`/${directory(facet)}?facetsRefinements[${facet.key}][0]=${record[facet.key]}`"
                      @click.native="scrollToTop"
                      v-else
                    >{{ record[facet.key] }}</router-link>
                  </p>
                  <template v-if="facet.type === 'array'">
                    <p v-for="(item, index) in record[facet.key]" :key="index">
                      <router-link
                        :to="`/${directory(facet)}?query=${item}`"
                        @click.native="scrollToTop"
                        v-if="facet.crossdirectory"
                      >{{ item }}</router-link>
                      <router-link
                        :to="`/${directory(facet)}?facetsRefinements[${facet.key}][0]=${item}`"
                        @click.native="scrollToTop"
                        v-else
                      >{{ item }}</router-link>
                      <span v-if="index != (record[facet.key].length - 1)">, </span>
                    </p>
                  </template>
                  <template v-if="facet.type === 'tree'">
                    <p v-for="(item, index) in record[facet.key]['lvl0']" :key="index">
                      <span v-if="index == (record[facet.key]['lvl0'].length - 1)">
                        {{ item }}
                        (<span v-for="(year, yindex) in record[facet.key]['lvl1']" :key="yindex">{{ year.split(' > ')[1] }}<span v-if="yindex != (record[facet.key]['lvl1'].length - 1)">, </span></span>)
                      </span>
                      <span v-else>
                        {{ item }}
                        <span v-for="(year, yindex) in record[facet.key]['lvl1']" :key="yindex">
                          {{ year.split(' > ')[1] }}
                          <span v-if="yindex != (record[facet.key]['lvl1'].length - 1)">,</span>
                        </span>
                      </span>
                    </p>
                  </template>
                </div>
              </div>

              <div
                v-for="link in validLinks"
                :key="link.key"
              >
                <router-link :to="`/${directory(link)}?facetsRefinements[${link.crossdirectory_facet}][0]=${record.name}`">
                  View all {{ link.name }}
                </router-link>
              </div>

              <div v-if="record.sponsor && record.locations.length > 1">
                <h3 class="title">Other Locations</h3>
                <span v-for="(index, location) in record.locations" :key="index">
                  {{ location.address || '' }}
                  {{ location.city || '' }}
                  {{ location.state || '' }}
                  {{ location.zip || '' }}
                </span>
              </div>
            </div>
            <div
              v-if="currentDirectory.fields.bio && record[currentDirectory.fields.bio.key]"
              class="content column is-12"
            >
              <hr>
              <h2>{{ currentDirectory.fields.bio.name }}</h2>
              <p v-html="record.bio"></p>
            </div>
          </div>
          <div
            class="column is-5 has-text-centered profile-section"
            :style="record.image_uri ? '' : 'order: 3'"
          >
            <div id="profile-sharing" class="buttons is-right is-hidden-touch">
              <router-link :to="{ name: 'expert' }" class="button is-info" v-if="expert.enabled && !record.expert">
                Become an Expert
              </router-link>
              <Sharing></Sharing>
            </div>
            <a :href="$store.state.banner_ad_link" v-if="$store.state.banner_ad_link && $store.state.banner_ad_image">
              <img :src="$store.state.banner_ad">
            </a>
          </div>
        </div>
      </div>
    </section>
  </ais-index>
</template>

<script>
/* eslint-disable import/no-unresolved */
import SearchHero from '@/components/SearchHero.vue';
import Sharing from '@/components/Sharing.vue';
import AlgoliaSearch from 'algoliasearch';
import { gmapApi } from 'vue2-google-maps';

export default {
  data() {
    return {
      record: {
        awards: {
          lvl0: [],
        },
        locations: [
          {
            phone: '',
            address: '',
            city: '',
            state: '',
            zip: '',
            position: {},
            preloaded: true,
          },
        ],
      },
    };
  },
  computed: {
    index() {
      if (this.$route.params.directory) return this.currentDirectory.algolia_index;
      return '';
    },
    markers() {
      return Object.keys(this.record.locations).map(f => this.record.locations[f].position);
    },
    currentDirectory() {
      return this.$store.state.directories[this.$route.params.directory.toLowerCase()];
    },
    directoryBadge() { return this.currentDirectory.badge_text; },
    facets() { return this.$store.getters.facets(this.$route.params.directory); },
    links() { return this.$store.getters.links(this.$route.params.directory); },
    google: gmapApi,
    secondaryDataPoint() {
      if (Array.isArray(this.record[this.currentDirectory.secondary_search_data_point])) {
        return this.record[this.currentDirectory.secondary_search_data_point][0];
      } else if (typeof this.record[this.currentDirectory.secondary_search_data_point] === 'string') {
        return this.record[this.currentDirectory.secondary_search_data_point];
      }
      return '';
    },
    expert() { return this.$store.state.expert; },
    validFacets() {
      return this.facets.filter(facet => this.record[facet.key]
        && Object.keys(this.record[facet.key]).length > 0
        && (this.record.sponsor || facet.free));
    },
    validLinks() {
      return this.links.filter(link => this.record.sponsor || link.free);
    },
  },
  created() {
    this.initAlgolia();
    this.initGoogleMap();
  },
  methods: {
    directory(field) {
      if (field.crossdirectory) return field.crossdirectory;
      return this.$route.params.directory;
    },
    hostname(uri) {
      let hostname;
      /* eslint-disable prefer-destructuring */
      if (uri.indexOf('//') > -1) {
        hostname = uri.split('/')[2];
      } else {
        hostname = uri.split('/')[0];
      }
      hostname = hostname.split('?')[0];
      return hostname;
    },
    website(url) {
      if (!/^(f|ht)tps?:\/\//i.test(url)) {
        return `http://${url}`;
      }
      return url;
    },
    initGoogleMap() {
      if (!this.google || !this.google.maps || this.record.locations[0].preloaded) {
        setTimeout(() => this.initGoogleMap(), 50);
        return;
      }

      const bounds = new this.google.maps.LatLngBounds();
      this.markers.forEach(marker => bounds.extend(marker));

      this.$refs.mapRef.$mapPromise.then((map) => {
        map.fitBounds(bounds);
        map.setCenter(bounds.getCenter());
      });
    },
    initAlgolia() {
      const client = AlgoliaSearch(
        this.$store.state.algolia_app_id,
        this.$store.state.algolia_api_key,
      );

      // Check if the directory exists before initializing the index
      if (this.currentDirectory === undefined) this.$router.push('/404');

      // Set up the index, which we later use to search for the record
      const index = client.initIndex(this.currentDirectory.algolia_index);

      // Search for the record via Algolia
      // If it exists, use the first result for the view, otherwise return a 404
      index.search(this.$route.params.record, (err, content) => {
        if (!(content && content.hits && content.hits.length > 0)) this.$router.push('/404');
        this.setRecord(content.hits[0]);
      });
    },
    setRecord(record) {
      this.record = record;
      if (!record.sponsor) this.record.locations = [record.locations[0]];
    },
    scrollToTop() {
      window.scrollTo(0, 0);
    },
  },
  components: {
    SearchHero,
    Sharing,
  },
};
</script>
